/**
 * Gestión → Créditos (`/gestion/creditos`).
 *
 * Puerto de `Credito::status` + `views/menu/credito/status.php`: el **estado de la
 * línea de crédito de cada cliente** —línea, cargos, pagos, disponible, balance,
 * vencido y por canjear— con el detalle de sus facturas a crédito y de los pagos
 * recibidos.
 *
 * Reglas conservadas: la línea es `credito.total + flexibilidad`, los cargos son
 * las facturas a crédito del cliente (sin las pagadas por adelantado, anuladas ni
 * notas de crédito), el vencido es el pendiente no pagado de las facturas ya
 * vencidas y `disponible = línea − cargos + pagos`.
 *
 * Crear o editar la línea, aprobar facturas, registrar pagos y canjear vales siguen
 * en el panel anterior (candado al pie).
 */

import {
  AlertTriangle,
  Eye,
  FileDown,
  HandCoins,
  LineChart,
  Info,
  Lock,
  Pencil,
  Plus,
  ReceiptText,
  RefreshCw,
  Scale,
  Search,
  Wallet,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { MenuAcciones } from '@/components/ui/menu-acciones';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { CampoFecha } from '@/components/ui/calendario';
import { Paginador } from '@/components/ui/paginador';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { ApiError } from '@/lib/http/api-error';
import { formatCurrency, cn } from '@/lib/utils';

import { cierreApi, useCredito, useCreditos, useCreditosCatalogos } from '../cierre.api';
import { CreditoBalanceDialog } from '../components/credito-balance-dialog';
import { CreditoCanjearDialog } from '../components/credito-canjear-dialog';
import { CreditoEditarDialog } from '../components/credito-editar-dialog';
import { CreditoNuevoDialog } from '../components/credito-nuevo-dialog';
import { CreditoPagoDialog } from '../components/credito-pago-dialog';
import { CreditoReportePagosDialog } from '../components/credito-reporte-pagos-dialog';
import { fechaCorta, hoyIso, monto } from '../gestion-formato';
import type { Credito, FiltrosCreditos } from '../cierre-gestion.types';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Detalle de un crédito: facturas y pagos. */
function DetalleCredito({
  id,
  filtros,
  onClose,
  onEditar,
}: {
  id: number | null;
  filtros: FiltrosCreditos;
  onClose: () => void;
  /** Abre el diálogo de edición del crédito abierto. */
  onEditar: (id: number) => void;
}) {
  const consulta = useCredito(id, filtros);
  const catalogos = useCreditosCatalogos();
  const datos = consulta.data;

  /** Nombre del producto de un descuento (`credito.descuento`). */
  const nombreProducto = (productoId: number): string =>
    catalogos.data?.productos.find((producto) => producto.id === productoId)?.nombre ??
    `Producto #${productoId}`;

  return (
    <Dialog
      open={id !== null}
      onClose={onClose}
      size="xl"
      title={datos ? `Crédito · ${datos.credito.cliente}` : 'Crédito'}
      description={datos ? `${datos.credito.numero_doc} · ${datos.credito.producto}` : undefined}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cerrar
          </Button>
          {datos ? (
            <Button
              variant="outline"
              size="sm"
              leftIcon={<Pencil className="size-4" />}
              onClick={() => onEditar(datos.credito.id)}
            >
              Editar crédito
            </Button>
          ) : null}
        </>
      }
    >
      {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

      {consulta.isError ? (
        <Alert variant="error" title="No se pudo abrir el crédito">
          {consulta.error instanceof ApiError ? consulta.error.message : 'Inténtalo de nuevo.'}
        </Alert>
      ) : null}

      {datos ? (
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-4">
            {[
              ['Límite', formatCurrency(datos.credito.limite)],
              ['Flexibilidad', formatCurrency(datos.credito.flexibilidad)],
              ['Línea', formatCurrency(datos.credito.linea)],
              ['Cargos', formatCurrency(datos.credito.cargos)],
              ['Pagos', formatCurrency(datos.credito.pagos)],
              ['Disponible', formatCurrency(datos.credito.disponible)],
              ['Balance', formatCurrency(datos.credito.balance)],
              ['Vencido', formatCurrency(datos.credito.vencido)],
              ['Por canjear', formatCurrency(datos.credito.por_canjear)],
              ['Estado', datos.credito.credito_estado ?? '—'],
              [
                'Descuentos',
                datos.credito.aplica_descuento
                  ? datos.credito.descuentos.length
                    ? datos.credito.descuentos
                        .map(
                          (descuento) =>
                            `${nombreProducto(descuento.producto_id)}: ${descuento.descuento.toLocaleString('es-PE', { minimumFractionDigits: 2 })}`,
                        )
                        .join(' · ')
                    : 'Aplica, sin productos'
                  : 'No aplica',
              ],
              ['Código externo', datos.credito.external_code_edsuite ?? '—'],
              ['Creado por', datos.credito.creado_por ?? '—'],
            ].map(([etiqueta, valor]) => (
              <div key={etiqueta}>
                <dt className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                  {etiqueta}
                </dt>
                <dd className="text-sm font-semibold text-ink-800">{valor}</dd>
              </div>
            ))}
          </div>

          <section>
            <h3 className="mb-2 text-sm font-semibold text-ink-800">
              Facturas a crédito ({datos.facturas.length})
            </h3>
            {datos.facturas.length === 0 ? (
              <p className="text-sm text-ink-500">
                Sin facturas a crédito en el rango ({fechaCorta(datos.desde)} – {fechaCorta(datos.hasta)}).
              </p>
            ) : (
              <div className="max-h-[32vh] overflow-auto rounded-card border border-ink-100">
                <table className="w-full border-collapse text-[13px]">
                  <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                    <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                      <th className="px-2 py-2 whitespace-nowrap">Comprobante</th>
                      <th className="px-2 py-2 whitespace-nowrap">Emisión</th>
                      <th className="px-2 py-2 whitespace-nowrap">Vence</th>
                      <th className="px-2 py-2 text-right whitespace-nowrap">Total</th>
                      <th className="px-2 py-2 text-right whitespace-nowrap">Retención</th>
                      <th className="px-2 py-2 text-right whitespace-nowrap">Pendiente</th>
                      <th className="px-2 py-2 text-right whitespace-nowrap">Pagado</th>
                      <th className="px-2 py-2 text-right whitespace-nowrap">Vencido</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.facturas.map((factura) => (
                      <tr key={factura.id} className="border-t border-ink-100">
                        <td className="px-2 py-2 whitespace-nowrap font-semibold text-ink-800">
                          {factura.comprobante}
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap">
                          {fechaCorta(factura.fecha_emision)}
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap">
                          {factura.vencida ? (
                            <Badge variant="danger">{fechaCorta(factura.fecha_vencimiento)}</Badge>
                          ) : (
                            fechaCorta(factura.fecha_vencimiento)
                          )}
                        </td>
                        <td className="px-2 py-2 text-right whitespace-nowrap">
                          {formatCurrency(factura.total)}
                        </td>
                        <td className="px-2 py-2 text-right whitespace-nowrap">
                          {formatCurrency(factura.retencion)}
                        </td>
                        <td className="px-2 py-2 text-right whitespace-nowrap">
                          {formatCurrency(factura.pendiente)}
                        </td>
                        <td className="px-2 py-2 text-right whitespace-nowrap">
                          {formatCurrency(factura.pagado)}
                        </td>
                        <td className="px-2 py-2 text-right whitespace-nowrap font-semibold">
                          {factura.vencido > 0 ? (
                            <span className="text-rose-700">{formatCurrency(factura.vencido)}</span>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section>
            <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink-800">
              <Wallet className="size-4 text-brand-500" aria-hidden="true" />
              Pagos recibidos ({datos.pagos.length})
            </h3>
            {datos.pagos.length === 0 ? (
              <p className="text-sm text-ink-500">Sin pagos en el rango consultado.</p>
            ) : (
              <ul className="grid gap-2 sm:grid-cols-2">
                {datos.pagos.map((pago, indice) => (
                  <li
                    key={`${pago.referencia ?? 'pago'}-${indice}`}
                    className="rounded-control border border-ink-100 bg-white px-3 py-2 text-sm"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-ink-900">
                        {pago.metodo ?? 'Pago'} {pago.referencia ? `· ${pago.referencia}` : ''}
                      </span>
                      <span className="font-semibold text-emerald-700">
                        {formatCurrency(pago.monto)}
                      </span>
                    </div>
                    <p className="text-xs text-ink-500">
                      {fechaCorta(pago.fecha)}
                      {pago.cuenta ? ` · ${pago.cuenta}` : ''}
                    </p>
                    {pago.reparto?.length ? (
                      <p
                        className="truncate text-xs text-ink-500"
                        title={pago.reparto
                          .map(
                            (item) =>
                              `${item.factura ?? 's/n'}: ${formatCurrency(item.monto)}`,
                          )
                          .join(' · ')}
                      >
                        Aplicado a {pago.reparto.length} factura
                        {pago.reparto.length === 1 ? '' : 's'}
                        {pago.detalle ? ` · ${pago.detalle}` : ''}
                      </p>
                    ) : pago.detalle ? (
                      <p className="text-xs text-ink-500">{pago.detalle}</p>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      ) : null}
    </Dialog>
  );
}

export function CreditosPage() {
  useDocumentTitle('Gestión · Créditos');

  // Como el panel anterior, el cálculo parte del 02/09/2022 (ver `INICIO_POR_DEFECTO`
  // en la API) y llega hasta hoy.
  const [desde, setDesde] = useState('2022-09-02');
  const [hasta, setHasta] = useState(hoyIso());
  const [busqueda, setBusqueda] = useState('');
  const [soloVencidos, setSoloVencidos] = useState(false);
  const [soloConBalance, setSoloConBalance] = useState(false);
  const [detalle, setDetalle] = useState<number | null>(null);
  const [editar, setEditar] = useState<number | null>(null);
  const [balance, setBalance] = useState<number | null>(null);
  const [canje, setCanje] = useState<number | null>(null);
  const [pago, setPago] = useState<number | null>(null);
  const [nueva, setNueva] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [errorPdf, setErrorPdf] = useState<string | null>(null);
  const [descargando, setDescargando] = useState(false);
  const [reporte, setReporte] = useState(false);

  const filtros = useMemo<FiltrosCreditos>(
    () => ({
      desde,
      hasta,
      search: busqueda.trim(),
      con_vencido: soloVencidos ? '1' : '',
      con_balance: soloConBalance ? '1' : '',
    }),
    [desde, hasta, busqueda, soloVencidos, soloConBalance],
  );

  const consulta = useCreditos(filtros);
  const creditos = consulta.data?.creditos ?? [];
  const resumen = consulta.data?.resumen;
  const paginacion = usePaginacion(creditos, { clave: filtros });

  const filtrosDetalle = useMemo<FiltrosCreditos>(() => ({ desde, hasta }), [desde, hasta]);

  /** Descarga el estado de créditos en PDF con los filtros de la pantalla. */
  const descargarPdf = async () => {
    setErrorPdf(null);
    setDescargando(true);

    try {
      await cierreApi.pdfCreditos(filtros);
    } catch (fallo) {
      setErrorPdf(fallo instanceof Error ? fallo.message : 'No se pudo generar el PDF.');
    } finally {
      setDescargando(false);
    }
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Créditos</h1>
          <p className="mt-1 text-sm text-ink-500">
            Estado de la línea de cada cliente: cargos, pagos, disponible, balance y vencido.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            leftIcon={<Plus className="size-4" />}
            onClick={() => setNueva(true)}
          >
            Nueva línea
          </Button>

          <Button
            variant="outline"
            size="sm"
            leftIcon={<LineChart className="size-4" aria-hidden="true" />}
            onClick={() => setReporte(true)}
          >
            Reporte de pagos
          </Button>

          <Button
            variant="outline"
            size="sm"
            loading={descargando}
            leftIcon={<FileDown className="size-4" aria-hidden="true" />}
            onClick={() => void descargarPdf()}
          >
            PDF
          </Button>

          <Button
            variant="outline"
            size="sm"
            leftIcon={
              <RefreshCw
                className={cn('size-4', consulta.isFetching && 'animate-spin')}
                aria-hidden="true"
              />
            }
            onClick={() => void consulta.refetch()}
          >
            Actualizar
          </Button>
        </div>
      </header>

      {aviso ? (
        <Alert variant="success" title="Listo">
          {aviso}
        </Alert>
      ) : null}

      {errorPdf ? (
        <Alert variant="error" title="No se pudo generar el PDF">
          {errorPdf}
        </Alert>
      ) : null}

      {resumen ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
          {[
            { etiqueta: 'Líneas', valor: String(resumen.total), detalle: `${resumen.filtrados} con los filtros` },
            { etiqueta: 'Línea total', valor: formatCurrency(resumen.linea), detalle: 'Crédito otorgado' },
            { etiqueta: 'Cargos', valor: formatCurrency(resumen.cargos), detalle: `${formatCurrency(resumen.pagos)} pagados` },
            { etiqueta: 'Balance', valor: formatCurrency(resumen.balance), detalle: `${resumen.con_balance} clientes con saldo` },
            { etiqueta: 'Vencido', valor: formatCurrency(resumen.vencido), detalle: `${resumen.con_vencido} clientes vencidos` },
          ].map((tarjeta) => (
            <Card key={tarjeta.etiqueta}>
              <CardContent className="p-4">
                <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                  {tarjeta.etiqueta}
                </p>
                <p className="text-lg font-bold text-ink-900">{tarjeta.valor}</p>
                <p className="text-xs text-ink-500">{tarjeta.detalle}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : null}

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1 text-xs text-ink-500">
              Desde
              <CampoFecha
               
                className={CONTROL}
                value={desde}
                onChange={(evento) => setDesde(evento.target.value)}
               tipo="date" />
            </label>
            <label className="flex flex-col gap-1 text-xs text-ink-500">
              Hasta
              <CampoFecha
               
                className={CONTROL}
                value={hasta}
                onChange={(evento) => setHasta(evento.target.value)}
               tipo="date" />
            </label>
            <label className="flex flex-col gap-1 text-xs text-ink-500 sm:col-span-2">
              Cliente o documento
              <span className="relative">
                <Search
                  className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-400"
                  aria-hidden="true"
                />
                <input
                  className={cn(CONTROL, 'pl-9')}
                  placeholder="Nombre o número de documento…"
                  value={busqueda}
                  onChange={(evento) => setBusqueda(evento.target.value)}
                />
              </span>
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-xs text-ink-600">
              <input
                type="checkbox"
                className="size-4 rounded border-ink-300"
                checked={soloVencidos}
                onChange={(evento) => setSoloVencidos(evento.target.checked)}
              />
              Sólo con vencido
            </label>
            <label className="flex items-center gap-2 text-xs text-ink-600">
              <input
                type="checkbox"
                className="size-4 rounded border-ink-300"
                checked={soloConBalance}
                onChange={(evento) => setSoloConBalance(evento.target.checked)}
              />
              Sólo con balance
            </label>
            <span className="ml-auto text-xs text-ink-500">
              {resumen?.filtrados ?? 0} cliente{(resumen?.filtrados ?? 0) === 1 ? '' : 's'}
            </span>
          </div>

          {consulta.isError ? (
            <Alert variant="error" title="No se pudo cargar el estado de los créditos">
              {consulta.error instanceof ApiError
                ? consulta.error.message
                : 'Inténtalo de nuevo en unos segundos.'}
            </Alert>
          ) : null}

          <p className="text-right text-xs text-ink-500">Importes en soles (S/)</p>

          <div className="max-h-[58vh] overflow-auto rounded-card border border-ink-100">
            <table className="w-full border-collapse text-[13px]">
              <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                  <th className="px-2 py-2">Cliente</th>
                  <th className="px-2 py-2 text-right">Línea</th>
                  <th className="px-2 py-2 text-right">Cargos</th>
                  <th className="px-2 py-2 text-right">Pagos</th>
                  <th className="px-2 py-2 text-right">Disponible</th>
                  <th
                    className="px-2 py-2 text-right"
                    title="Saldo pendiente y, debajo en rojo, el importe vencido"
                  >
                    Saldo / vencido
                  </th>
                  <th className="px-2 py-2 text-right">Opciones</th>
                </tr>
              </thead>
              <tbody>
                {consulta.isLoading
                  ? Array.from({ length: 5 }).map((_, fila) => (
                      <tr key={`esqueleto-${fila}`} className="border-t border-ink-100">
                        {Array.from({ length: 7 }).map((__, columna) => (
                          <td key={`celda-${columna}`} className="px-2 py-3">
                            <Skeleton className="h-4 w-full" />
                          </td>
                        ))}
                      </tr>
                    ))
                  : null}

                {!consulta.isLoading && creditos.length === 0 ? (
                  <tr className="border-t border-ink-100">
                    <td colSpan={7} className="px-3 py-10 text-center text-sm text-ink-500">
                      No hay créditos que cumplan los filtros.
                    </td>
                  </tr>
                ) : null}

                {paginacion.visibles.map((credito: Credito) => (
                  <tr key={credito.id} className="border-t border-ink-100 hover:bg-brand-50/30">
                    <td className="max-w-[15rem] px-2 py-2">
                      <p className="truncate font-semibold text-ink-900" title={credito.cliente}>
                        {credito.cliente}
                      </p>
                      <p className="truncate text-xs text-ink-500">
                        {credito.numero_doc}
                        {credito.producto !== 'Todos' ? ` · ${credito.producto}` : ''}
                        {credito.tipo_credito ? ` · ${credito.tipo_credito}` : ''}
                      </p>
                    </td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">{monto(credito.linea)}</td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">{monto(credito.cargos)}</td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">{monto(credito.pagos)}</td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      <span className="font-semibold">{monto(credito.disponible)}</span>
                      {credito.por_canjear > 0 ? (
                        <span className="block text-xs text-ink-500">
                          canje {monto(credito.por_canjear)}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      {credito.balance > 0 ? (
                        <span className="font-semibold">{monto(credito.balance)}</span>
                      ) : (
                        <span className="text-ink-500">0.00</span>
                      )}
                      {credito.vencido > 0 ? (
                        <span className="flex items-center justify-end gap-1 text-xs font-semibold text-rose-700">
                          <AlertTriangle className="size-3.5" aria-hidden="true" />
                          {monto(credito.vencido)}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-2 py-2 text-right">
                      <div className="flex justify-end">
                        <MenuAcciones
                          etiqueta={`Acciones del crédito de ${credito.cliente}`}
                          acciones={[
                            {
                              etiqueta: 'Editar crédito',
                              icono: <Pencil className="size-3.5" aria-hidden="true" />,
                              onClick: () => setEditar(credito.id),
                            },
                            {
                              etiqueta: 'Balance del cliente',
                              icono: <Scale className="size-3.5" aria-hidden="true" />,
                              onClick: () => setBalance(credito.id),
                            },
                            {
                              etiqueta: 'Canje de vales',
                              icono: <HandCoins className="size-3.5" aria-hidden="true" />,
                              onClick: () => setCanje(credito.id),
                            },
                            {
                              etiqueta: 'Pagos y facturas',
                              icono: <ReceiptText className="size-3.5" aria-hidden="true" />,
                              onClick: () => setPago(credito.id),
                            },
                            {
                              etiqueta: 'Facturas y pagos del crédito',
                              icono: <Eye className="size-3.5" aria-hidden="true" />,
                              onClick: () => setDetalle(credito.id),
                            },
                          ]}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Paginador {...paginacion} etiqueta="clientes" className="mt-3" />
        </CardContent>
      </Card>

      <Alert variant="info" title="Pendiente en Créditos">
        <ul className="mt-1 ml-5 list-disc space-y-1 text-sm">
          <li>
            <span className={ICONO_EN_ALERTA}>
              <Lock className="size-3.5" aria-hidden="true" />
              Aprobar facturas y anular comprobantes a crédito (`note_post`):
              pertenece a Logística → Notas de Crédito y al mantenimiento de
              comprobantes, no a esta pantalla.
            </span>
          </li>
        </ul>
      </Alert>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        Los importes se calculan con las facturas a crédito y los pagos del rango elegido; la fecha
        de inicio por defecto es la misma que usaba el panel anterior (02/09/2022). El lápiz de cada
        fila edita el límite, el estado y los descuentos por producto del crédito, como el modal
        «Actualización de Crédito» del panel anterior. La balanza abre el **balance** del cliente
        (saldo inicial, cargos, abonos y saldo al corte), las manos con monedas el **canje de
        vales** (vales pendientes por liquidar y pre-cálculo de los comprobantes) y el billete los
        **pagos / liquidación de facturas** del crédito.
      </p>

      <DetalleCredito
        id={detalle}
        filtros={filtrosDetalle}
        onClose={() => setDetalle(null)}
        onEditar={(id) => {
          setDetalle(null);
          setEditar(id);
        }}
      />

      <CreditoBalanceDialog
        key={`balance-${balance ?? 0}`}
        id={balance}
        onClose={() => setBalance(null)}
        onPagar={(id) => {
          setBalance(null);
          setPago(id);
        }}
      />

      <CreditoNuevoDialog
        open={nueva}
        onClose={() => setNueva(false)}
        onAviso={(mensaje) => {
          setAviso(mensaje);
          setTimeout(() => setAviso(null), 8000);
        }}
      />

      <CreditoPagoDialog
        key={`pago-${pago ?? 0}`}
        id={pago}
        onClose={() => setPago(null)}
        onAviso={(mensaje) => {
          setAviso(mensaje);
          setTimeout(() => setAviso(null), 6000);
        }}
      />

      <CreditoReportePagosDialog open={reporte} onClose={() => setReporte(false)} />

      <CreditoCanjearDialog
        key={`canje-${canje ?? 0}`}
        id={canje}
        onClose={() => setCanje(null)}
        onAviso={(mensaje) => {
          setAviso(mensaje);
          setTimeout(() => setAviso(null), 8000);
        }}
      />

      <CreditoEditarDialog
        key={editar ?? 'sin-credito'}
        id={editar}
        filtros={filtrosDetalle}
        onClose={() => setEditar(null)}
        onEliminado={() => void consulta.refetch()}
        onAviso={(mensaje) => {
          setAviso(mensaje);
          setTimeout(() => setAviso(null), 6000);
        }}
      />
    </div>
  );
}
